import { useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || '';

const supabase = createClient(supabaseUrl, supabaseAnonKey);

/**
 * 会話記憶の型定義
 */
export interface RelationshipMemory {
  id: string;
  user_id: string;
  memory_type: string;
  content: string;
  importance_level: number;
  created_at: string;
  updated_at: string;
}

/**
 * Supabaseから会話記憶を管理するカスタムフック
 */
export function useSupabaseRelationMemory(userId: string) {
  const [memories, setMemories] = useState<RelationshipMemory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isValidUserId = Boolean(userId && userId.trim() !== '' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(userId));

  /**
   * 記憶を取得
   */
  const fetchMemories = useCallback(async () => {
    if (!isValidUserId) return [];
    try {
      const { data, error: fetchError } = await supabase
        .from('relationship_memory')
        .select('*')
        .eq('user_id', userId)
        .order('importance_level', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(50);

      if (fetchError) throw fetchError;
      setMemories(data || []);
      return data || [];
    } catch (err) {
      console.error('記憶の取得に失敗しました:', err);
      setError(err instanceof Error ? err.message : '不明なエラー');
      return [];
    }
  }, [userId, isValidUserId]);

  /**
   * 記憶を追加
   */
  const addMemory = useCallback(async (
    data: Omit<RelationshipMemory, 'id' | 'user_id' | 'created_at' | 'updated_at'>
  ): Promise<boolean> => {
    if (!isValidUserId) return false;
    try {
      const { error: insertError } = await supabase
        .from('relationship_memory')
        .insert({
          user_id: userId,
          ...data,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });

      if (insertError) throw insertError;

      await fetchMemories();
      return true;
    } catch (err) {
      console.error('記憶の追加に失敗しました:', err);
      setError(err instanceof Error ? err.message : '不明なエラー');
      return false;
    }
  }, [userId, isValidUserId, fetchMemories]);

  /**
   * 記憶を削除
   */
  const deleteMemory = useCallback(async (memoryId: string): Promise<boolean> => {
    if (!isValidUserId) return false;
    try {
      const { error: deleteError } = await supabase
        .from('relationship_memory')
        .delete()
        .eq('id', memoryId);

      if (deleteError) throw deleteError;

      await fetchMemories();
      return true;
    } catch (err) {
      console.error('記憶の削除に失敗しました:', err);
      setError(err instanceof Error ? err.message : '不明なエラー');
      return false;
    }
  }, [isValidUserId, fetchMemories]);

  /**
   * 記憶を更新
   */
  const updateMemory = useCallback(async (
    memoryId: string,
    updates: Partial<Omit<RelationshipMemory, 'id' | 'user_id' | 'created_at' | 'updated_at'>>
  ): Promise<boolean> => {
    if (!isValidUserId) return false;
    try {
      const { error: updateError } = await supabase
        .from('relationship_memory')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', memoryId);

      if (updateError) throw updateError;

      await fetchMemories();
      return true;
    } catch (err) {
      console.error('記憶の更新に失敗しました:', err);
      setError(err instanceof Error ? err.message : '不明なエラー');
      return false;
    }
  }, [isValidUserId, fetchMemories]);

  /**
   * 初回読み込み
   */
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setError(null);
      if (isValidUserId) {
        await fetchMemories();
      }
      setIsLoading(false);
    };

    loadData();
  }, [fetchMemories, isValidUserId]);

  return {
    memories,
    isLoading,
    error,
    fetchMemories,
    addMemory,
    deleteMemory,
    updateMemory,
  };
}